import { router } from '@inertiajs/react'
import Layout from '../../components/Layout'
import MeetingKpiCard from '../../components/dashboard/MeetingKpiCard'
import ScoreboardKpiCard from '../../components/dashboard/ScoreboardKpiCard'
import RocksKpiCard from '../../components/dashboard/RocksKpiCard'
import ActionItemsKpiCard from '../../components/dashboard/ActionItemsKpiCard'
import NextMeetingCard from '../../components/dashboard/NextMeetingCard'
import ScoreboardSummaryCard from '../../components/dashboard/ScoreboardSummaryCard'
import ActionItemsSummaryCard from '../../components/dashboard/ActionItemsSummaryCard'
import RocksSummaryCard from '../../components/dashboard/RocksSummaryCard'
import TopicsSummaryCard from '../../components/dashboard/TopicsSummaryCard'
import TeamMembersCard from '../../components/dashboard/TeamMembersCard'
import { sortPeople } from '../../utils/peopleSort'
import type {
  MeetingStats,
  ScoreboardStats,
  RocksStats,
  ActionItemStats,
  TopicStats,
  TeamMember,
} from '../../types'

interface AgendaTemplateSummary {
  id: number
  name: string
  total_duration_minutes: number
}

interface Props {
  team: { id: number; name: string; description: string | null; member_count: number }
  active_tab: string
  meeting_stats: MeetingStats
  scoreboard_stats: ScoreboardStats
  rocks_stats: RocksStats
  action_item_stats: ActionItemStats
  topic_stats: TopicStats
  team_members: TeamMember[]
  agenda_templates: AgendaTemplateSummary[]
  can_start_meeting: boolean
}

export default function Overview({
  team,
  active_tab,
  meeting_stats,
  scoreboard_stats,
  rocks_stats,
  action_item_stats,
  topic_stats,
  team_members,
  agenda_templates,
  can_start_meeting,
}: Props) {
  const handleStartMeeting = (templateId?: number) => {
    router.post(`/teams/${team.id}/start_meeting`, templateId ? { agenda_template_id: templateId } : {})
  }

  return (
    <Layout team={team} activeTab={active_tab}>
      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold text-text-primary">{team.name}</h1>
            {team.description && (
              <p className="text-base text-text-secondary mt-1">{team.description}</p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {sortPeople(team_members).slice(0, 6).map((m) => (
                <div
                  key={m.id}
                  className="w-9 h-9 rounded-full bg-charcoal/10 text-text-primary text-sm font-bold flex items-center justify-center border-2 border-white"
                  title={`${m.first_name} ${m.last_name}`}
                >
                  {m.initials}
                </div>
              ))}
              {team_members.length > 6 && (
                <div className="w-9 h-9 rounded-full bg-stone text-text-secondary text-sm font-bold flex items-center justify-center border-2 border-white">
                  +{team_members.length - 6}
                </div>
              )}
            </div>

            {can_start_meeting && !meeting_stats.meeting_in_progress && agenda_templates.length > 0 && (
              <button
                onClick={() => handleStartMeeting(agenda_templates[0]?.id)}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-pinnacle-red text-white text-base font-semibold rounded-lg hover:bg-pinnacle-red/90 transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {agenda_templates[0]?.name ? `Start ${agenda_templates[0].name}` : 'Start Meeting'}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <MeetingKpiCard stats={meeting_stats} teamId={team.id} />
          <ScoreboardKpiCard stats={scoreboard_stats} teamId={team.id} />
          <RocksKpiCard stats={rocks_stats} teamId={team.id} />
          <ActionItemsKpiCard stats={action_item_stats} teamId={team.id} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3 space-y-6">
            <NextMeetingCard stats={meeting_stats} teamId={team.id} agendaTemplates={agenda_templates} canStartMeeting={can_start_meeting} />
            <ScoreboardSummaryCard stats={scoreboard_stats} teamId={team.id} />
            <ActionItemsSummaryCard stats={action_item_stats} teamId={team.id} />
          </div>

          <div className="lg:col-span-2 space-y-6">
            <RocksSummaryCard stats={rocks_stats} teamId={team.id} />
            <TopicsSummaryCard stats={topic_stats} teamId={team.id} />
            <TeamMembersCard members={team_members} teamId={team.id} />
          </div>
        </div>
      </div>
    </Layout>
  )
}
